import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Megaphone, RefreshCw, Send, X } from 'lucide-react';
import { type ApprovalDecision, type BroadcastItem, type BroadcastStatus } from '../api/adminOps';
import { useAuth } from '../components/AuthProvider';
import {
  DetailDrawer,
  QueueTable,
  ReasonActionBar,
  StatusChip,
  type QueueColumn,
} from '../components/ui';
import {
  useBroadcast,
  useBroadcastApproval,
  useBroadcastAudiencePreview,
  useBroadcasts,
  useCancelBroadcast,
  useCreateBroadcast,
  useDecideApproval,
  useRequestBroadcastApproval,
  useTestBroadcast,
} from '../hooks/useAdminOps';
import {
  approvalGate,
  type ApprovalLookupState,
  audienceEstimateNote,
  BODY_MAX_LENGTH,
  canCancel,
  canRequestApproval,
  composeBlocker,
  deliveryProgress,
  statusChip,
  testSendNote,
  testTargetBlocker,
  TITLE_MAX_LENGTH,
} from './broadcasts.logic';

/**
 * The account a test was last addressed to. Remembered per browser because it
 * is the operator's own app account and retyping it on every draft is the
 * friction that stops anyone testing at all.
 */
const TEST_TARGET_STORAGE_KEY = 'donatemate.admin.broadcastTestEmail';

function readStoredTestTarget(): string {
  try {
    return window.localStorage.getItem(TEST_TARGET_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function storeTestTarget(email: string): void {
  try {
    window.localStorage.setItem(TEST_TARGET_STORAGE_KEY, email);
  } catch {
    // A browser refusing storage is not a reason to fail the send.
  }
}

const STATUS_FILTERS = [
  'draft',
  'pending_approval',
  'scheduled',
  'sending',
  'completed',
  'cancelled',
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number] | 'all';

const AUDIENCES = [
  { value: 'all_users', label: 'Everyone' },
  { value: 'users_with_active_device', label: 'Only users with an active device' },
] as const;

function chip(status: BroadcastStatus) {
  const config = statusChip(status);
  return <StatusChip label={config.label} tone={config.tone} />;
}

export default function Broadcasts() {
  const { user, hasRole } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<string>(AUDIENCES[0].value);
  const [reason, setReason] = useState('');
  const [testEmail, setTestEmail] = useState<string>(readStoredTestTarget);

  const queue = useBroadcasts(statusFilter === 'all' ? {} : { status: statusFilter });
  // Polls only while the selected broadcast is still moving.
  const detail = useBroadcast(selectedId);
  const preview = useBroadcastAudiencePreview(audience, composing);

  const createBroadcast = useCreateBroadcast();
  const requestApproval = useRequestBroadcastApproval();
  const testBroadcast = useTestBroadcast();
  const cancelBroadcast = useCancelBroadcast();
  const decideApproval = useDecideApproval();

  // Read from the live query, not the row that was clicked, so the drawer's
  // counters move while a send is running.
  const selected = detail.data?.data ?? queue.rows.find(row => row.id === selectedId) ?? null;
  const blocker = composeBlocker({ title, body, audience });
  const testBlocker = testTargetBlocker(testEmail);

  // The approval decision is fetched only while one is actually outstanding.
  const approvals = useBroadcastApproval(selectedId, selected?.status === 'pending_approval');
  const pendingApproval = approvals.data ?? null;
  // A failed lookup is not an absent request: saying "none" on an error would
  // tell the second operator the send needs no decision.
  const lookupState: ApprovalLookupState = approvals.isLoading
    ? 'loading'
    : approvals.isError
      ? 'error'
      : 'ready';
  const gate = approvalGate(
    pendingApproval,
    user?.sub,
    hasRole('admin', 'admin-operator'),
    lookupState
  );

  const columns: QueueColumn<BroadcastItem>[] = [
    {
      key: 'title',
      header: 'Title',
      render: row => <span className="font-medium">{row.title}</span>,
    },
    { key: 'status', header: 'Status', render: row => chip(row.status) },
    {
      key: 'audience',
      header: 'Audience',
      render: row => (row.audience === 'all_users' ? 'Everyone' : 'Active devices'),
    },
    {
      key: 'delivery',
      header: 'Delivered',
      render: row => {
        const progress = deliveryProgress(row);
        return (
          <span className="tabular-nums">
            {progress.resolved.toLocaleString()}
            {progress.total > 0 ? ` / ${progress.total.toLocaleString()}` : ''}
            {progress.percent === null ? '' : ` (${progress.percent}%)`}
          </span>
        );
      },
    },
    {
      key: 'created',
      header: 'Created',
      render: row => formatDistanceToNow(new Date(row.created_at), { addSuffix: true }),
    },
  ];

  const resetCompose = () => {
    setTitle('');
    setBody('');
    setAudience(AUDIENCES[0].value);
    setComposing(false);
  };

  const submitDraft = async () => {
    await createBroadcast.mutateAsync({ title, body, audience });
    resetCompose();
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Broadcasts
          </h1>
          <p className="text-gray-text text-sm mt-1">
            One message to every user. A second operator has to approve the send.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => queue.refetch()}>
            <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button className="btn-primary" onClick={() => setComposing(true)}>
            New broadcast
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', ...STATUS_FILTERS] as StatusFilter[]).map(value => (
          <button
            key={value}
            onClick={() => setStatusFilter(value)}
            className={`px-3 py-1 rounded text-sm ${
              statusFilter === value ? 'bg-primary text-dark' : 'bg-dark-lighter text-gray-text'
            }`}
          >
            {value === 'all' ? 'All' : statusChip(value as BroadcastStatus).label}
          </button>
        ))}
      </div>

      <QueueTable
        columns={columns}
        rows={queue.rows}
        rowKey={row => row.id}
        isLoading={queue.isPending}
        onRowClick={row => setSelectedId(row.id)}
        emptyTitle="No broadcasts yet"
        emptyHint="Compose one to announce a change to every user."
        hasNextPage={queue.hasNextPage}
        isFetchingNextPage={queue.isFetchingNextPage}
        onLoadMore={() => queue.fetchNextPage()}
      />

      {composing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="glass-card w-full max-w-2xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">New broadcast</h2>

            <div>
              <label className="label">Title</label>
              <input
                className="input w-full"
                value={title}
                onChange={event => setTitle(event.target.value)}
                placeholder="What changed"
              />
              <p className="text-xs text-gray-text mt-1 tabular-nums">
                {title.length} / {TITLE_MAX_LENGTH}
              </p>
            </div>

            <div>
              <label className="label">Body</label>
              <textarea
                className="input w-full h-28"
                value={body}
                onChange={event => setBody(event.target.value)}
                placeholder="One or two sentences. This is what lands on the lock screen."
              />
              <p className="text-xs text-gray-text mt-1 tabular-nums">
                {body.length} / {BODY_MAX_LENGTH}
              </p>
            </div>

            <div>
              <label className="label">Audience</label>
              <select
                className="input w-full"
                value={audience}
                onChange={event => setAudience(event.target.value)}
              >
                {AUDIENCES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {preview.data && (
                <p className="text-xs text-gray-text mt-1">
                  {audienceEstimateNote(preview.data.data.estimated_recipients)}
                </p>
              )}
            </div>

            <p className="text-xs text-gray-text">
              Push and in-app only. Email is off by default and needs an explicit quota check.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" onClick={resetCompose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={blocker !== null || createBroadcast.isPending}
                onClick={submitDraft}
              >
                {blocker ?? 'Save draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DetailDrawer
        open={selected !== null}
        title={selected?.title ?? 'Broadcast'}
        onClose={() => setSelectedId(null)}
        footer={
          selected && (
            <div className="space-y-3">
              {canRequestApproval(selected.status) && (
                <div className="space-y-2">
                  <label className="label">Reason for sending</label>
                  <input
                    className="input w-full"
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    placeholder="Why this message, to everyone, now"
                  />
                  <button
                    className="btn-primary w-full"
                    disabled={reason.trim().length === 0 || requestApproval.isPending}
                    onClick={async () => {
                      await requestApproval.mutateAsync({ id: selected.id, reason });
                      setReason('');
                    }}
                  >
                    <Send className="w-4 h-4" /> Request approval
                  </button>
                  <p className="text-xs text-gray-text">
                    A different operator has to approve this on the Approvals page. You cannot
                    approve your own request.
                  </p>
                </div>
              )}

              {selected.status === 'pending_approval' && (
                <div className="space-y-2 border-t border-gray-border pt-3">
                  <label className="label">Second-operator approval</label>
                  {gate.canDecide ? (
                    <ReasonActionBar
                      reasons={[
                        { value: 'approved', label: 'Approve — schedule this send' },
                        { value: 'rejected', label: 'Reject — return it to draft' },
                      ]}
                      actionLabel="Submit decision"
                      destructive
                      isPending={decideApproval.isPending}
                      notePlaceholder="Optional decision note for the audit trail"
                      onSubmit={(decision, note) => {
                        if (pendingApproval === null) return;
                        decideApproval.mutate({
                          id: pendingApproval.id,
                          decision: decision as ApprovalDecision,
                          note,
                        });
                      }}
                    />
                  ) : null}
                  <p className="text-xs text-gray-text">{gate.note}</p>
                  {lookupState === 'error' && (
                    <button className="btn-secondary w-full" onClick={() => approvals.refetch()}>
                      Retry
                    </button>
                  )}
                  {pendingApproval && (
                    <p className="text-xs text-gray-text">
                      Requested by{' '}
                      <span className="font-mono break-all">{pendingApproval.requested_by}</span>,
                      expires {new Date(pendingApproval.expires_at).toLocaleString()}. It is also on
                      the Approvals page.
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <label className="label">Send a test to</label>
                <input
                  className="input w-full"
                  value={testEmail}
                  onChange={event => setTestEmail(event.target.value)}
                  placeholder="app-account@example.com"
                />
                <button
                  className="btn-secondary w-full"
                  disabled={testBlocker !== null || testBroadcast.isPending}
                  onClick={() => {
                    const target = testEmail.trim();
                    storeTestTarget(target);
                    testBroadcast.mutate({ id: selected.id, targetEmail: target });
                  }}
                >
                  {testBroadcast.isPending ? 'Sending test…' : (testBlocker ?? 'Send a test')}
                </button>
                <p className="text-xs text-gray-text">{testSendNote(testBroadcast)}</p>
                <p className="text-xs text-gray-text">
                  Admin sign-in and app accounts are separate, so name the app account by the email
                  it signs in with — your admin address will not resolve.
                </p>
              </div>

              {canCancel(selected.status) && (
                <button
                  className="btn-ghost w-full text-red-400"
                  disabled={cancelBroadcast.isPending}
                  onClick={() => cancelBroadcast.mutate(selected.id)}
                >
                  <X className="w-4 h-4" /> Cancel broadcast
                </button>
              )}
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">{chip(selected.status)}</div>

            <p className="text-gray-text whitespace-pre-wrap">{selected.body}</p>

            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-gray-text">Recipients</dt>
                <dd className="tabular-nums">{selected.recipient_count.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-text">Sent</dt>
                <dd className="tabular-nums">{selected.sent_count.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-text">Suppressed</dt>
                <dd className="tabular-nums">{selected.suppressed_count.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-text">Failed</dt>
                <dd className="tabular-nums">{selected.failed_count.toLocaleString()}</dd>
              </div>
            </dl>

            <p className="text-xs text-gray-text">
              Suppressed means the user turned app announcements off, has no active device, or the
              broadcast was cancelled before reaching them. Quiet hours delay a push rather than
              dropping it, so a delivered count can keep rising after the send finishes.
            </p>

            {selected.approved_by && (
              <p className="text-xs text-gray-text">
                Approved by {selected.approved_by === user?.sub ? 'you' : selected.approved_by}.
              </p>
            )}

            {selected.error_message && (
              <p className="text-xs text-red-400">{selected.error_message}</p>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}
